import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import { getRememberMePreference } from "../session-policy";
import "./realtime-safety";
import { LiveTabSessions, reconcileTabRevision, isRemoteTabResetting, sessionRevision } from "./liveTabs";
export { isRemoteTabResetting } from "./liveTabs";

let supabaseInstance: SupabaseClient | null = null;
let liveTabs: LiveTabSessions | null = null;
export function restoreLiveTabSession() { return liveTabs?.restore() ?? Promise.resolve(null); }
export function invalidateLiveTabSession() { liveTabs?.invalidate(); }
export function acceptsLiveTabSession(session: import("@supabase/supabase-js").Session) {
  return !liveTabs || liveTabs.accepts(session);
}
export const PROD_SUPABASE_AUTH_STORAGE_KEY =
  "sb-vtsxidkjdffqlnpcoutc-auth-token";

/**
 * Task #3065 — Web hardening.
 *
 * When `VITE_AUTH_HTTPONLY_REFRESH=true`, the web client stops
 * persisting the Supabase session (refresh token included) to
 * localStorage. The AuthProvider then runs the session through the
 * HttpOnly-cookie proxy at `/api/auth/proxy/*` instead, so the
 * refresh token is never reachable from JavaScript. The flag is a
 * staged-rollout gate — when off (the default), behaviour is
 * unchanged so the live site keeps working.
 */
export function isHttpOnlyRefreshEnabled(): boolean {
  const v = import.meta.env.VITE_AUTH_HTTPONLY_REFRESH;
  return v === "true" || v === true || v === "1";
}

/**
 * Remember-me-aware storage adapter (Task #3061).
 *
 * Supabase Auth shards its session across keys like `sb-<project>-auth-token`.
 * When the user opts INTO "Remember me" we write to `localStorage` (the
 * historical default — the session survives a browser close). When the
 * user opts OUT we write to `sessionStorage`, so the refresh token is
 * dropped the instant the browser process exits.
 *
 * Reads check `sessionStorage` first, then fall back to `localStorage`.
 * Writes go to exactly one tier and proactively remove any stale copy
 * in the other tier — that's what stops "I unchecked Remember me but
 * the old localStorage token is still there" from defeating the toggle.
 *
 * When the HttpOnly-cookie proxy (Task #3065) is enabled, Supabase's
 * own session persistence is disabled entirely, so this adapter is a
 * no-op in that mode (storage isn't passed in).
 */
export const rememberMeStorage: Storage = {
  get length(): number {
    try { return (sessionStorage?.length || 0) + (localStorage?.length || 0); } catch { return 0; }
  },
  clear(): void {
    try { sessionStorage?.clear(); } catch { /* noop */ }
    try { localStorage?.clear(); } catch { /* noop */ }
  },
  key(i: number): string | null {
    try {
      const sLen = sessionStorage?.length || 0;
      if (i < sLen) return sessionStorage.key(i);
      return localStorage?.key(i - sLen) ?? null;
    } catch { return null; }
  },
  getItem(key: string): string | null {
    if (isRemoteTabResetting()) return null;
    try {
      const fromSession = sessionStorage?.getItem(key);
      if (fromSession !== null && fromSession !== undefined) return fromSession;
    } catch { /* noop */ }
    try { return localStorage?.getItem(key) ?? null; } catch { return null; }
  },
  setItem(key: string, value: string): void {
    let remember = getRememberMePreference();
    // A later preference edit in another (possibly anonymous) tab must not
    // promote an already non-remembered session when its token rotates.
    try {
      const next = JSON.parse(value);
      for (const [store, persistent] of [[sessionStorage, false], [localStorage, true]] as const) {
        const raw = store.getItem(key);
        if (!raw) continue;
        const previous = JSON.parse(raw);
        if (sessionRevision(next) && sessionRevision(next) === sessionRevision(previous)) {
          remember = persistent;
          break;
        }
      }
    } catch { /* Non-session keys (e.g. PKCE verifier) use the explicit choice. */ }
    if (remember) {
      try { localStorage?.setItem(key, value); } catch { /* noop */ }
      try { sessionStorage?.removeItem(key); } catch { /* noop */ }
    } else {
      try { sessionStorage?.setItem(key, value); } catch { /* noop */ }
      try { localStorage?.removeItem(key); } catch { /* noop */ }
    }
  },
  removeItem(key: string): void {
    try { sessionStorage?.removeItem(key); } catch { /* noop */ }
    if (!isRemoteTabResetting()) {
      try { localStorage?.removeItem(key); } catch { /* noop */ }
    }
  },
};

/**
 * Tab-local promise-chain mutex for Supabase auth-token access.
 *
 * supabase-js v2 serialises getSession / token-refresh calls across tabs
 * via the Web Locks API (navigator.locks). When two concurrent callers race
 * within the same tab the browser grants the lock to one and rejects the
 * other with:
 *   "Lock "lock:sb-…-auth-token" was released because another request stole it"
 *
 * Supplying a custom `auth.lock` replaces navigator.locks entirely for this
 * tab. The implementation is a simple promise chain: each new caller appends
 * its work to the tail of the chain so only one refresh runs at a time,
 * eliminating the cross-tab race without breaking any auth flows.
 */
let lockChain: Promise<void> = Promise.resolve();

export function createTabLock() {
  return function tabLock<T>(
    _name: string,
    _acquireTimeout: number,
    fn: () => Promise<T>,
  ): Promise<T> {
    let releaseLock!: () => void;
    const acquired = new Promise<void>((resolve) => {
      releaseLock = resolve;
    });

    const result = lockChain.then(() => fn());

    lockChain = lockChain
      .then(() => result)
      .then(
        () => { releaseLock(); },
        () => { releaseLock(); },
      );

    void acquired;
    return result;
  };
}

/** Legacy sessions share rotating refresh credentials. Coordinate their SDK
 * work across tabs without auth-js's zero-timeout/steal behavior; cancellation
 * is bounded and never steals a lock held by another tab. Cookie mode retains
 * its existing tab-only lock.
 */
export function createLiveTabLock() {
  const local = createTabLock();
  return function liveTabLock<T>(name: string, acquireTimeout: number, fn: () => Promise<T>): Promise<T> {
    return local(name, acquireTimeout, async () => {
      let locks: LockManager | undefined;
      try { locks = typeof navigator === "undefined" ? undefined : navigator.locks; } catch { /* restricted browser */ }
      if (!locks) return fn();
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 6000);
      try {
        return await locks.request(`bac:live-auth:${name}`, { signal: controller.signal }, () => {
          clearTimeout(timer);
          return fn();
        });
      } finally { clearTimeout(timer); }
    });
  };
}

/**
 * Bound an auth-client promise so a hung supabase.auth.* call can't leave
 * the UI spinning forever.
 *
 * The promise-chain lock above is the real deadlock fix, but as a safety
 * net every post-login client step (setSession / getSession / cookie
 * adoption) is wrapped in this so a stalled call rejects within a bounded
 * time. Callers then clear their loading state and surface a retryable
 * error instead of an indefinite spinner. The underlying work still
 * settles on its own — we just stop awaiting it past `ms`.
 */
export function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`${label} timed out after ${ms}ms`));
    }, ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

export function getSupabaseClient(): SupabaseClient | null {
  if (supabaseInstance) return supabaseInstance;

  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!url || !anonKey) {
    console.warn(
      "Supabase credentials not configured. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.",
    );
    return null;
  }

  const httpOnly = isHttpOnlyRefreshEnabled();
  if (!httpOnly && typeof window !== "undefined") reconcileTabRevision(PROD_SUPABASE_AUTH_STORAGE_KEY);

  supabaseInstance = createClient(url, anonKey, {
    auth: {
      // When the HttpOnly-cookie proxy is enabled, disable Supabase's
      // own session persistence + auto-refresh: the refresh token now
      // lives in an HttpOnly cookie owned by the API server, and the
      // SPA pulls a fresh access token from `/api/auth/proxy/refresh`
      // on a timer managed by AuthProvider. Leaving these `true`
      // would race the client into writing the refresh token back to
      // localStorage on every TOKEN_REFRESHED.
      autoRefreshToken: !httpOnly,
      persistSession: !httpOnly,
      detectSessionInUrl: true,
      // Keep the project-scoped key stable when production moves from the raw
      // Supabase hostname to the custom auth host. Without this, existing web
      // sessions remain stranded under the pre-cutover key.
      storageKey: PROD_SUPABASE_AUTH_STORAGE_KEY,
      // Remember-me adapter only when local persistence is in play.
      storage: !httpOnly && typeof window !== "undefined" ? rememberMeStorage : undefined,
      // Both modes serialize within the tab and avoid auth-js's stealing
      // lock implementation. Legacy tabs additionally coordinate rotating
      // shared credentials using bounded, non-stealing Web Locks. HttpOnly
      // refresh remains cookie-owned with its existing tab-only mutex.
      lock: httpOnly ? createTabLock() : createLiveTabLock(),
    },
  });
  if (!httpOnly && typeof window !== "undefined") {
    liveTabs = new LiveTabSessions(supabaseInstance, () => {
      try {
        if (sessionStorage.getItem(PROD_SUPABASE_AUTH_STORAGE_KEY)) return false;
        if (localStorage.getItem(PROD_SUPABASE_AUTH_STORAGE_KEY)) return true;
      } catch { /* Restricted storage never changes the explicit choice. */ }
      return getRememberMePreference();
    });
  }

  return supabaseInstance;
}
